import React, { createContext, useContext, useMemo, useState } from 'react';
import { getReportingMonth, type ReportingMonth } from '../lib/reportingMonth';

export type ReportNurseNotesView = 'monthly' | 'trimester' | 'year';

export type NavShellFiltersState = {
  /** Client filter for privileged incident pages (Med Error / Fall). Empty = all departments. */
  incidentDeptNavFilterIds: string[];
  setIncidentDeptNavFilterIds: (ids: string[]) => void;
  /** Client filter for privileged team pages (/team, /team/ethic). Empty = all allowed options. */
  teamDeptNavFilterIds: string[];
  setTeamDeptNavFilterIds: (ids: string[]) => void;
  /**
   * Canonical compliance category id from team-mode Dashboard (drives shell department list scope).
   * Null when not on team dashboard or before mount.
   */
  teamShellComplianceCategoryId: string | null;
  setTeamShellComplianceCategoryId: (id: string | null) => void;
  /**
   * Active รายงานรวม category from ComplianceHub (/report). Drives sticky dept filter for nurse_notes.
   */
  reportComplianceSummaryCategoryId: string | null;
  setReportComplianceSummaryCategoryId: (id: string | null) => void;
  /** /report + nurse_notes: sticky shell dept filter (synced into SupervisorReportingFiltersContext). */
  reportNurseNotesDeptNavFilterIds: string[];
  setReportNurseNotesDeptNavFilterIds: (ids: string[]) => void;
  /** /report + nurse_notes: sticky มุมมอง. Lives on the shell because the report provider is below the layout. */
  reportNurseNotesView: ReportNurseNotesView;
  setReportNurseNotesView: (view: ReportNurseNotesView) => void;
  /** /report + nurse_notes: sticky ปี / ไตรมาส / เดือนรายงาน. */
  reportNurseNotesReportingMonth: ReportingMonth;
  setReportNurseNotesReportingMonth: (month: ReportingMonth) => void;
  /** /report + ethicSurveys or orgClimateSurveys: sticky shell dept filter (synced into SupervisorReportingFiltersContext). */
  reportEthicDeptNavFilterIds: string[];
  setReportEthicDeptNavFilterIds: (ids: string[]) => void;
};

const NavShellFiltersContext = createContext<NavShellFiltersState | null>(null);

export function NavShellFiltersProvider({ children }: { children: React.ReactNode }) {
  const [incidentDeptNavFilterIds, setIncidentDeptNavFilterIds] = useState<string[]>([]);
  const [teamDeptNavFilterIds, setTeamDeptNavFilterIds] = useState<string[]>([]);
  const [teamShellComplianceCategoryId, setTeamShellComplianceCategoryId] = useState<string | null>(null);
  const [reportComplianceSummaryCategoryId, setReportComplianceSummaryCategoryId] = useState<string | null>(null);
  const [reportNurseNotesDeptNavFilterIds, setReportNurseNotesDeptNavFilterIds] = useState<string[]>([]);
  const [reportNurseNotesView, setReportNurseNotesView] = useState<ReportNurseNotesView>('year');
  const [reportNurseNotesReportingMonth, setReportNurseNotesReportingMonth] = useState<ReportingMonth>(() =>
    getReportingMonth(new Date()),
  );
  const [reportEthicDeptNavFilterIds, setReportEthicDeptNavFilterIds] = useState<string[]>([]);

  const value = useMemo(
    () => ({
      incidentDeptNavFilterIds,
      setIncidentDeptNavFilterIds,
      teamDeptNavFilterIds,
      setTeamDeptNavFilterIds,
      teamShellComplianceCategoryId,
      setTeamShellComplianceCategoryId,
      reportComplianceSummaryCategoryId,
      setReportComplianceSummaryCategoryId,
      reportNurseNotesDeptNavFilterIds,
      setReportNurseNotesDeptNavFilterIds,
      reportNurseNotesView,
      setReportNurseNotesView,
      reportNurseNotesReportingMonth,
      setReportNurseNotesReportingMonth,
      reportEthicDeptNavFilterIds,
      setReportEthicDeptNavFilterIds,
    }),
    [
      incidentDeptNavFilterIds,
      teamDeptNavFilterIds,
      teamShellComplianceCategoryId,
      reportComplianceSummaryCategoryId,
      reportNurseNotesDeptNavFilterIds,
      reportNurseNotesView,
      reportNurseNotesReportingMonth,
      reportEthicDeptNavFilterIds,
    ],
  );

  return <NavShellFiltersContext.Provider value={value}>{children}</NavShellFiltersContext.Provider>;
}

export function useNavShellFilters(): NavShellFiltersState {
  const ctx = useContext(NavShellFiltersContext);
  if (!ctx) {
    throw new Error('useNavShellFilters must be used within NavShellFiltersProvider');
  }
  return ctx;
}
